import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NewSearchView } from './views/NewSearchView';
import { ResultsView } from './views/ResultsView';
import { ReviewView } from './views/ReviewView';
import type { Criterion, SearchResult } from './types';
import type { SearchDoc } from './api';
import { getSearch } from './api';

type View = 'new' | 'results' | 'review';

interface ActiveSearch {
  search: SearchDoc;
  filters: Record<string, unknown>;
  criteria: Criterion[];
  matchCount: number;
}

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const idParam = searchParams.get('id');
  const [view, setView] = useState<View>(idParam ? 'results' : 'new');
  const [active, setActive] = useState<ActiveSearch | null>(null);
  const [reviewResults, setReviewResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // URL → state: load a search when ?id appears or changes (e.g. via Recent Searches sidebar).
  useEffect(() => {
    if (!idParam) {
      // No id in URL — back to fresh new-search view.
      setActive(null);
      setView('new');
      setLoadError(null);
      return;
    }
    if (active?.search._id === idParam) return; // already loaded
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getSearch(idParam)
      .then(s => {
        if (cancelled) return;
        setActive({
          search: s,
          filters: (s.filters as Record<string, unknown>) || {},
          criteria: s.criteria || [],
          matchCount: s.totalHits || 0,
        });
        setView('results');
      })
      .catch(e => {
        console.warn('[SearchPage] load error:', e);
        if (cancelled) return;
        setLoadError(e?.response?.data?.error || e?.message || 'Could not load search');
        setActive(null);
        setView('new');
        // Don't strip the bad id from URL automatically — let the user retry/correct.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // We intentionally only depend on idParam — when active updates internally we don't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam]);

  return (
    <div className="flex h-full flex-col bg-[var(--canvas-base)] text-[var(--text-primary)]">
      {loading ? (
        <div className="flex flex-1 items-center justify-center text-[var(--text-muted)]">
          Loading search…
        </div>
      ) : loadError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-12 text-center text-[var(--text-muted)]">
          <div className="text-base">Could not load this search.</div>
          <div className="text-[13px]">{loadError}</div>
          <button
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--accent-strong)]"
            onClick={() => setSearchParams({})}
          >
            Start a new search
          </button>
        </div>
      ) : view === 'new' ? (
        <NewSearchView
          onRun={(state) => {
            setActive(state);
            setSearchParams({ id: state.search._id });
            setView('results');
          }}
        />
      ) : view === 'results' && active ? (
        <ResultsView
          search={active.search}
          filters={active.filters}
          criteria={active.criteria}
          matchCount={active.matchCount}
          onChange={(next) => setActive((cur) => (cur ? { ...cur, ...next } : cur))}
          onNewSearch={() => {
            setActive(null);
            setReviewResults([]);
            setSearchParams({});
            setView('new');
          }}
          onReview={(results) => {
            setReviewResults(results);
            setView('review');
          }}
        />
      ) : view === 'review' && active ? (
        <ReviewView
          search={active.search}
          results={reviewResults}
          criteria={active.criteria}
          totalCount={active.matchCount}
          onBack={() => setView('results')}
        />
      ) : null}
    </div>
  );
}
